import { calculateDiscount } from "../../utils/calculateDiscount.js";
import { roundToPlaces } from "../../utils/roundToPlaces.js";
import { Country } from "../Country.js";
import type { CountrySettings } from "../CountrySettings.js";
import { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { ProductForCustomer } from "../Product.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import type { SubscriptionParamsForPricing } from "../Subscription.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { calculatePriceOfSubscriptionWithoutDiscounts } from "./calculatePriceOfSubscriptionWithoutDiscounts.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { calculatePriceWithoutTax } from "./calculatePriceWithoutTax.js";
import { getMembershipTaxRate } from "./getMembershipTaxRate.js";
import { membershipPricePerCurrencyMap } from "./membershipPricePerCurrencyMap.js";
import { subscriptionDiscountPercentForCzechiaPerSizeMap } from "./subscriptionDiscountPercentForCzechiaPerSizeMap.js";

export function calculatePriceOfRecurringSubscription({
	subcategories,
	destinationCountry,
	subscription,
	mothersBlendProduct,
	cafeinaProduct,
	currency,
	vatType,
	exchangeRates,
	countrySettings,
	mothersBlendConstantPriceEnabled,
}: {
	subcategories: SubcategoryForCustomer[];
	destinationCountry: Country | "outsideEu";
	subscription: SubscriptionParamsForPricing;
	mothersBlendProduct: ProductForCustomer;
	cafeinaProduct: ProductForCustomer;
	currency: Currency;
	vatType: EconomicSubjectVatType;
	exchangeRates: ExchangeRate[];
	countrySettings: CountrySettings[];
	mothersBlendConstantPriceEnabled: boolean;
}): TaxedPrice {
	const base = calculatePriceOfSubscriptionWithoutDiscounts({
		subcategories,
		destinationCountry,
		subscription,
		mothersBlendProduct,
		cafeinaProduct,
		currency,
		vatType,
		exchangeRates,
		mothersBlendConstantPriceEnabled,
		deliveryCount: undefined,
	});

	const membershipPriceWithTax = membershipPricePerCurrencyMap[currency];
	const membershipPriceWithoutTax = calculatePriceWithoutTax(
		membershipPriceWithTax,
		getMembershipTaxRate(destinationCountry, vatType, countrySettings),
	);

	let withoutTax = roundToPlaces(
		destinationCountry === Country.Czechia
			? calculateDiscount(base.withoutTax, subscriptionDiscountPercentForCzechiaPerSizeMap[subscription.size])
			: base.withoutTax,
		2,
	);
	let withTax = calculatePriceWithTax(withoutTax - membershipPriceWithoutTax, base.taxRate) + membershipPriceWithTax;

	if (currency === Currency.CZK) {
		withTax = Math.ceil(withTax);
		withoutTax = calculatePriceWithoutTax(withTax, base.taxRate);
	}

	return {
		taxRate: base.taxRate,
		withTax,
		withoutTax,
	};
}
